/**
 * Categorize feedback into themes using AI API
 * Similar to the approach in Journey.js (lines 691-714)
 */

export async function categorizeFeedback(feedbackArray) {
  if (!feedbackArray || feedbackArray.length === 0) {
    return [];
  }

  // Separate records with text (for categorization) from those without
  const recordsWithText = feedbackArray
    .filter(item => item.feedbackText && item.feedbackText.trim() !== '')
    .map((item, index) => ({
      id: item.id || index,
      text: item.feedbackText
    }));

  const recordsWithoutText = feedbackArray
    .filter(item => !item.feedbackText || item.feedbackText.trim() === '');

  // Prepare data for API - only records with text
  let categorizedWithText = [];
  
  if (recordsWithText.length > 0) {
    const prompt = {
      prompt:
        "Do not include any explanations, only provide a RFC8259 compliant JSON response following this format without deviation. " +
        "Categorize the following customer feedback into themes. " +
        "Return a JSON array where each item has 'id' (matching the input id) and 'theme' (the theme name). " +
        "Group similar feedback together under the same theme name. " +
        "Use concise, descriptive theme names (e.g., 'Bug Reports', 'Feature Requests', 'Performance Issues', 'UI/UX Feedback', 'Payment Issues', etc.). " +
        "Ignore false, blank or null entries. " +
        "Data: " + JSON.stringify(recordsWithText)
    };

    const options = {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(prompt),
    };

    try {
      // TODO: Replace with actual API endpoint
      // For now, return a mock response structure
      // const response = await fetch("YOUR_API_ENDPOINT", options);
      // const data = await response.json();
      // const categorized = JSON.parse(data.message.content);
      
      // Mock categorization for development
      categorizedWithText = mockCategorize(recordsWithText);
    } catch (error) {
      console.error("Error categorizing feedback:", error);
      // Return uncategorized items for records with text
      categorizedWithText = recordsWithText.map(item => ({
        id: item.id,
        theme: "Uncategorized",
        sentiment: analyzeSentiment(item.text)
      }));
    }
  }

  // Combine categorized records with text and uncategorized records without text
  const allCategorized = [
    ...categorizedWithText,
    ...recordsWithoutText.map(item => ({
      id: item.id,
      theme: "Uncategorized",
      sentiment: "neutral" // Default sentiment for records without text
    }))
  ];

  return allCategorized;
}

/**
 * Analyze sentiment of feedback text
 * Returns "positive", "negative", or "neutral"
 */
function analyzeSentiment(text) {
  const lowerText = text.toLowerCase();
  
  const positiveKeywords = [
    "love", "great", "excellent", "amazing", "fantastic", "wonderful", 
    "brilliant", "perfect", "best", "awesome", "thank", "thanks", 
    "appreciate", "happy", "pleased", "satisfied", "good", "nice", 
    "helpful", "easy", "simple", "quick", "fast", "enjoy", "enjoying"
  ];
  
  const negativeKeywords = [
    "hate", "terrible", "awful", "horrible", "worst", "bad", "poor", 
    "disappointed", "frustrated", "angry", "annoyed", "upset", "unhappy", 
    "useless", "rubbish", "garbage", "waste", "broken", "problem", 
    "issue", "complaint", "refund", "cancel", "disgusted", "furious"
  ];
  
  // Count positive and negative keyword matches
  const positiveCount = positiveKeywords.filter(keyword => 
    lowerText.includes(keyword)
  ).length;
  
  const negativeCount = negativeKeywords.filter(keyword => 
    lowerText.includes(keyword)
  ).length;
  
  // Determine sentiment based on keyword counts
  if (negativeCount > positiveCount) {
    return "negative";
  } else if (positiveCount > negativeCount) {
    return "positive";
  } else if (positiveCount > 0 || negativeCount > 0) {
    // If equal counts but both present, default to negative for complaints
    return "negative";
  }
  
  return "neutral";
}

/**
 * Mock categorization function for development
 * Enhanced with betting-specific categories and keyword matching
 */
function mockCategorize(feedbackData) {
  // Define keyword lists for each category (order matters - more specific first)
  const categoryKeywords = {
    "Withdrawal Issues": [
      "withdraw", "withdrawal", "withdrawals", "withdrawn", "cash out", 
      "payout", "payouts", "withdraw money", "get money", "money out", 
      "withdrawal problem", "can't withdraw", "withdrawal delay", 
      "withdrawal blocked", "can't cash out", "payout problem"
    ],
    "Deposit Issues": [
      "deposit", "deposits", "deposited", "payment", "pay", "paying", 
      "card", "payment method", "payment failed", "deposit failed", 
      "can't deposit", "deposit problem", "payment problem", 
      "card declined", "payment declined"
    ],
    "Odds Issues": [
      "odds", "odd", "unfair", "better odds", "odds not good", 
      "poor odds", "bad odds", "competitive odds", "value", "pricing", 
      "odds are", "odds too", "better value", "poor value"
    ],
    "Bonuses & Promotions": [
      "bonus", "bonuses", "free bet", "free bets", "promotion", 
      "promotions", "promo", "offer", "offers", "reward", "rewards", 
      "incentive", "freebie", "freebies"
    ],
    "Performance & Reliability": [
      "slow", "lag", "laggy", "freeze", "frozen", "crash", "crashes", 
      "crashed", "performance", "reliable", "reliability", "loading", 
      "load time", "buffering", "unresponsive", "not working", "broken", 
      "down", "offline", "hanging", "stuck"
    ],
    "Limited Options": [
      "not enough", "limited", "more options", "more matches", 
      "more events", "more markets", "bet types", "selection", 
      "variety", "choice", "options", "markets", "more games", 
      "more sports", "more betting", "lack of", "missing markets"
    ],
    "Feature Requests": [
      "feature", "features", "add", "want", "need", "would like", 
      "should have", "cut 1", "flexi", "2up", "request", "suggestion", 
      "improvement", "wish", "hoping for", "could have"
    ],
    "Bug Reports": [
      "bug", "bugs", "error", "errors", "broken", "not working", 
      "issue", "issues", "problem", "problems", "glitch", "glitches", 
      "fault", "faulty", "malfunction", "defect"
    ],
    "Account Issues": [
      "login", "log in", "sign in", "account", "password", "verify", 
      "verification", "access", "locked", "blocked", "suspended", 
      "can't log", "can't access", "account problem"
    ]
  };

  return feedbackData.map(item => {
    const text = item.text.toLowerCase();
    let theme = "Other";

    // Check categories in order (more specific first)
    for (const [category, keywords] of Object.entries(categoryKeywords)) {
      // Check if any keyword matches
      const matches = keywords.filter(keyword => text.includes(keyword));
      if (matches.length > 0) {
        theme = category;
        break; // Use first match (most specific category)
      }
    }

    // Analyze sentiment
    const sentiment = analyzeSentiment(item.text);

    return {
      id: item.id,
      theme: theme,
      sentiment: sentiment
    };
  });
}

/**
 * Generate unique theme ID from theme name
 */
export function generateThemeId(themeName) {
  return themeName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

